// src/pages/Admin.js
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import "../styles/Admin.css";

const isTrialing = (u) =>
  u?.role === "PRINCIPAL" &&
  String(u?.subscription_status || "").toUpperCase() === "TRIALING" &&
  (typeof u?.trial_days_left === "number" ? u.trial_days_left > 0 : true);

// human-readable sizes
const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const val = (bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1);
  return `${val} ${units[i]}`;
};

// Map backend website tech to a concise label
const wpTechLabel = (val) => {
  if (!val) return "N/A";
  const s = String(val).toLowerCase();
  if (s.includes("wordpress") && !s.includes("non") && !s.includes("not"))
    return "WordPress";
  return "NON-WordPress";
};

const Admin = () => {
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pendingPrincipals, setPendingPrincipals] = useState([]);
  const [selectedPrincipals, setSelectedPrincipals] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [doingBulk, setDoingBulk] = useState(false);
  const [error, setError] = useState("");

  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserRole, setNewUserRole] = useState("STAFF");
  const [extraDaysMap, setExtraDaysMap] = useState({}); // { [email]: number }

  // non-blocking banner (auto dismiss)
  const [banner, setBanner] = useState(null); // { type: 'success'|'error'|'info', text: string }
  const bannerTimerRef = useRef(null);
  const showBanner = (text, type = "success", ms = 4000) => {
    setBanner({ text, type });
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    bannerTimerRef.current = setTimeout(() => setBanner(null), ms);
  };

  // per-button busy flags
  // keys like 'approve:user@email', 'reject:user@email', 'delete:user@email', 'extend:user@email'
  const [rowBusy, setRowBusy] = useState({});
  const setBusy = (key, val) =>
    setRowBusy((prev) => ({
      ...prev,
      [key]: val,
    }));

  const navigate = useNavigate();
  const adminLoadedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    };
  }, []);

  const fetchAdminData = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    setTableLoading(true);
    setError("");

    try {
      const [usersRes, pendingRes] = await Promise.all([
        api.get("/auth/users"),
        api.get("/auth/pending-principals"),
      ]);

      if (!mountedRef.current) return;

      const u = usersRes.data || [];
      setUsers(u);
      setTotalUsers(u.length);
      setPendingPrincipals(pendingRes.data || []);

      const nextMap = {};
      u.forEach((x) => {
        if (x.role === "PRINCIPAL") nextMap[x.email] = 7;
      });
      setExtraDaysMap(nextMap);
    } catch (err) {
      if (!mountedRef.current) return;
      setError("Failed to load admin data.");
      console.error(err?.response?.data || err?.message);
    } finally {
      if (mountedRef.current) setTableLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (adminLoadedRef.current) return;
    adminLoadedRef.current = true;
    fetchAdminData();
  }, [fetchAdminData]);

  const handleSelectPrincipal = (email) => {
    setSelectedPrincipals((prev) =>
      prev.includes(email)
        ? prev.filter((e) => e !== email)
        : [...prev, email]
    );
  };

  const handleApprovePrincipal = async (email) => {
    const key = `approve:${email}`;
    setBusy(key, true);
    try {
      const { data } = await api.post(
        `/auth/approve-principal?principal_email=${encodeURIComponent(email)}`
      );
      showBanner(data?.message || "Principal approved.", "success");
      await fetchAdminData();
    } catch (err) {
      showBanner(
        "Failed to approve principal: " +
          (err?.response?.data?.detail || err?.message),
        "error"
      );
    } finally {
      setBusy(key, false);
    }
  };

  const handleRejectPrincipal = async (email) => {
    if (!window.confirm(`Are you sure you want to reject ${email}?`)) return;
    const key = `reject:${email}`;
    setBusy(key, true);
    try {
      const { data } = await api.post(
        `/auth/reject-principal?principal_email=${encodeURIComponent(email)}`
      );
      showBanner(data?.message || "Principal rejected.", "success");
      await fetchAdminData();
    } catch (err) {
      showBanner(
        "Failed to reject principal: " +
          (err?.response?.data?.detail || err?.message),
        "error"
      );
    } finally {
      setBusy(key, false);
    }
  };

  const handleApprovePrincipals = async () => {
    if (selectedPrincipals.length === 0) return;
    setDoingBulk(true);
    try {
      const results = await Promise.allSettled(
        selectedPrincipals.map((email) =>
          api.post(
            `/auth/approve-principal?principal_email=${encodeURIComponent(
              email
            )}`
          )
        )
      );

      const successes = results.filter((r) => r.status === "fulfilled").length;
      const failures = results
        .map((r, i) =>
          r.status === "rejected" ? selectedPrincipals[i] : null
        )
        .filter(Boolean);

      showBanner(
        `Approved: ${successes}${
          failures.length ? ` — Failed: ${failures.join(", ")}` : ""
        }`,
        failures.length ? "info" : "success"
      );
      setSelectedPrincipals([]);
      await fetchAdminData();
    } catch (err) {
      setError(
        "Failed to approve selected principals: " +
          (err?.response?.data?.detail || err?.message)
      );
      showBanner("Bulk approval failed.", "error");
    } finally {
      setDoingBulk(false);
    }
  };

  const handleRejectPrincipals = async () => {
    if (selectedPrincipals.length === 0) return;
    if (
      !window.confirm(
        "Are you sure you want to reject the selected principals?"
      )
    )
      return;

    setDoingBulk(true);
    try {
      const results = await Promise.allSettled(
        selectedPrincipals.map((email) =>
          api.post(
            `/auth/reject-principal?principal_email=${encodeURIComponent(
              email
            )}`
          )
        )
      );

      const successes = results.filter((r) => r.status === "fulfilled").length;
      const failures = results
        .map((r, i) =>
          r.status === "rejected" ? selectedPrincipals[i] : null
        )
        .filter(Boolean);

      showBanner(
        `Rejected: ${successes}${
          failures.length ? ` — Failed: ${failures.join(", ")}` : ""
        }`,
        failures.length ? "info" : "success"
      );
      setSelectedPrincipals([]);
      await fetchAdminData();
    } catch (err) {
      setError(
        "Failed to reject selected principals: " +
          (err?.response?.data?.detail || err?.message)
      );
      showBanner("Bulk rejection failed.", "error");
    } finally {
      setDoingBulk(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUserEmail) return;

    setActionLoading(true);
    try {
      const response = await api.post("/auth/create-user", {
        email: newUserEmail,
        role: newUserRole,
      });
      showBanner(response?.data?.message || "User created.", "success");
      setNewUserEmail("");
      await fetchAdminData();
    } catch (err) {
      const msg =
        "Failed to create user: " +
        (err?.response?.data?.detail || err?.message);
      setError(msg);
      showBanner(msg, "error");
    } finally {
      setActionLoading(false);
    }
  };

  // Two-step delete (preview -> confirm -> perform)
  const handleDeleteUser = async (email) => {
    const key = `delete:${email}`;
    try {
      // Step 1: preview
      const previewRes = await api.delete(
        `/auth/users/${encodeURIComponent(email)}`
      );
      const p = previewRes.data || {};

      const filesCount = p?.will_delete?.files?.count ?? 0;
      const missingCount = p?.will_delete?.files?.missing_count ?? 0;
      const bytes = p?.will_delete?.files?.total_size_bytes ?? 0;
      const photosCount = p?.will_delete?.db?.photos_count ?? 0;
      const resetCount = p?.will_delete?.db?.reset_codes_count ?? 0;

      const sampleExisting = Array.isArray(
        p?.will_delete?.files?.sample_existing
      )
        ? p.will_delete.files.sample_existing
        : [];
      const sampleMissing = Array.isArray(
        p?.will_delete?.files?.sample_missing
      )
        ? p.will_delete.files.sample_missing
        : [];

      const listExisting = sampleExisting
        .slice(0, 5)
        .map((s, i) => `  ${i + 1}. ${s}`)
        .join("\n");
      const listMissing = sampleMissing
        .slice(0, 3)
        .map((s, i) => `  ${i + 1}. ${s} (missing)`)
        .join("\n");

      const confirmMsg =
        "You are about to permanently delete:\n\n" +
        "• User: " +
        email +
        "\n" +
        "• Photos (DB rows): " +
        photosCount +
        "\n" +
        "• Reset codes: " +
        resetCount +
        "\n" +
        "• Files on disk: " +
        filesCount +
        " (" +
        formatBytes(bytes) +
        ")\n" +
        (missingCount
          ? "• Files referenced but missing: " + missingCount + "\n"
          : "") +
        "\n" +
        (listExisting ? "Sample existing files:\n" + listExisting + "\n\n" : "") +
        (listMissing ? "Sample missing references:\n" + listMissing + "\n\n" : "") +
        "This action cannot be undone.\n\nProceed?";

      const ok = window.confirm(confirmMsg);
      if (!ok) return;

      // Step 2: perform
      setBusy(key, true);
      await api.delete(
        `/auth/users/${encodeURIComponent(email)}?confirm=true`
      );
      showBanner(
        `User ${email} and related data were deleted.`,
        "success"
      );
      await fetchAdminData();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message;
      setError("Failed to delete user: " + msg);
      showBanner("Failed to delete user: " + msg, "error");
    } finally {
      setBusy(key, false);
    }
  };

  const handleExtendTrial = async (email) => {
    const days = Number(extraDaysMap[email] ?? 7);
    if (!Number.isFinite(days) || days < 1 || days > 30) {
      showBanner(
        "Please enter a number of days between 1 and 30.",
        "error"
      );
      return;
    }

    const key = `extend:${email}`;
    setBusy(key, true);
    try {
      const { data } = await api.post(
        `/auth/admin/extend-trial?principal_email=${encodeURIComponent(
          email
        )}&extra_days=${days}`
      );
      showBanner(
        data?.message || `Trial extended by ${days} day(s).`,
        "success"
      );
      await fetchAdminData();
    } catch (err) {
      showBanner(
        "Failed to extend trial: " +
          (err?.response?.data?.detail || err?.message),
        "error"
      );
    } finally {
      setBusy(key, false);
    }
  };

  // ---------- Readout ----------
  const readout = useMemo(() => {
    const principals = users.filter((u) => u.role === "PRINCIPAL");
    const byStatus = principals.reduce(
      (acc, u) => {
        const s = String(u.subscription_status || "UNKNOWN").toUpperCase();
        acc[s] = (acc[s] || 0) + 1;
        return acc;
      },
      { TRIALING: 0, ACTIVE: 0, INACTIVE: 0, UNKNOWN: 0 }
    );

    const expiringSoon = principals.filter((u) => {
      const d =
        typeof u.trial_days_left === "number" ? u.trial_days_left : null;
      const s = String(u.subscription_status || "").toUpperCase();
      return s === "TRIALING" && d !== null && d >= 0 && d <= 2;
    });

    return {
      principalsCount: principals.length,
      byStatus,
      expiringSoon,
    };
  }, [users]);

  if (tableLoading) return <div className="loading">Loading...</div>;

  return (
    <div className="admin-page">
      <div className="dashboard-container">
        <header className="admin-header">
          <div>
            <p className="admin-kicker">Internal tools</p>
            <h1 className="admin-title">Admin Dashboard</h1>
            <p className="admin-subtitle">
              Approve principals, manage users, and keep an eye on trials.
            </p>
          </div>
        </header>

        {/* Hard error (e.g. load failure or action issues) */}
        {error && (
          <div
            className="admin-error-inline"
            role="alert"
            aria-live="assertive"
          >
            {error}
          </div>
        )}

        {/* Banner (success / info / extra feedback) */}
        {banner && (
          <div
            className={`admin-banner admin-banner--${banner.type}`}
            role="status"
            aria-live="polite"
          >
            {banner.text}
          </div>
        )}

        {/* Overview */}
        <section className="admin-section">
          <h2 className="admin-section-title">Overview</h2>
          <div className="admin-metrics-row">
            <div className="cardish">
              <strong>Total Users:</strong> {totalUsers}
            </div>
            <div className="cardish">
              <strong>Principals:</strong> {readout.principalsCount}
            </div>
            <div className="cardish">
              <strong>Trials:</strong> {readout.byStatus.TRIALING}
            </div>
            <div className="cardish">
              <strong>Active Subs:</strong> {readout.byStatus.ACTIVE}
            </div>
            <div className="cardish">
              <strong>Inactive:</strong> {readout.byStatus.INACTIVE}
            </div>
            <div className="cardish">
              <strong>Trials expiring ≤2 days:</strong>{" "}
              {readout.expiringSoon.length}
            </div>
          </div>
          {readout.expiringSoon.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary>Show expiring trials</summary>
              <ul style={{ marginTop: 8 }}>
                {readout.expiringSoon.map((u) => (
                  <li key={u.email}>
                    {u.email} — {u.trial_days_left} day(s) left
                    {u.trial_ends_at_iso
                      ? ` (ends ${new Date(
                        u.trial_ends_at_iso
                      ).toLocaleString()})`
                      : ""}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>

        {/* Create user */}
        <section className="admin-section">
          <h2 className="admin-section-title">Create New User</h2>
          <form
            onSubmit={handleCreateUser}
            className="admin-create-user-form"
          >
            <div className="admin-create-row">
              <input
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="Enter user email"
                required
                className="admin-input"
              />
              <select
                value={newUserRole}
                onChange={(e) => setNewUserRole(e.target.value)}
                className="admin-select"
              >
                <option value="ADMIN">Admin</option>
                <option value="PRINCIPAL">Principal</option>
                <option value="STAFF">Staff</option>
                <option value="PARENT">Parent</option>
              </select>
              <button
                type="submit"
                disabled={actionLoading}
                className="admin-btn admin-btn--primary"
              >
                {actionLoading ? (
                  <>
                    <span className="spinner" />
                    Creating…
                  </>
                ) : (
                  "Create User"
                )}
              </button>
            </div>
          </form>
        </section>

        {/* All users */}
        <section className="admin-section">
          <h2 className="admin-section-title">All Users</h2>
          {users.length > 0 ? (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Total Photos</th>
                    <th>Status</th>
                    <th>Display Name</th>
                    <th>School Name</th>
                    <th>School Website</th>
                    <th>Website Technology</th>
                    <th>Subscription</th>
                    <th>Trial Days Left</th>
                    <th>Trial Ends</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => {
                    const delKey = `delete:${user.email}`;
                    const extKey = `extend:${user.email}`;
                    const isPrincipal = user.role === "PRINCIPAL";

                    return (
                      <tr key={user.id ?? user.email}>
                        <td className="cell-ellipsis">{user.email}</td>
                        <td>{user.role}</td>
                        <td style={{ textAlign: "right" }}>
                          {user.total_photos}
                        </td>
                        <td>
                          <span
                            className={`chip ${
                              user.status === "ACTIVE"
                                ? "chip--ok"
                                : user.status === "PENDING"
                                  ? "chip--warn"
                                  : "chip--muted"
                            }`}
                          >
                            {user.status || "—"}
                          </span>
                        </td>
                        <td className="cell-ellipsis">
                          {user.display_name || "N/A"}
                        </td>
                        <td className="cell-ellipsis">
                          {user.school_name || "N/A"}
                        </td>
                        <td className="cell-ellipsis">
                          {user.school_website ? (
                            <a
                              href={user.school_website}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {user.school_website}
                            </a>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td>{wpTechLabel(user.website_tech_used)}</td>

                        {/* Subscription columns — only for Principals */}
                        <td style={{ textAlign: "center" }}>
                          {isPrincipal
                            ? String(
                              user.subscription_status || "—"
                            ).toUpperCase()
                            : "—"}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {isPrincipal &&
                          typeof user.trial_days_left === "number"
                            ? user.trial_days_left
                            : "—"}
                        </td>
                        <td>
                          {isPrincipal && user.trial_ends_at_iso
                            ? new Date(
                              user.trial_ends_at_iso
                            ).toLocaleString()
                            : "—"}
                        </td>

                        <td>
                          <div className="admin-actions">
                            <button
                              className="admin-btn admin-btn--danger"
                              onClick={() => handleDeleteUser(user.email)}
                              disabled={!!rowBusy[delKey]}
                            >
                              {rowBusy[delKey] ? (
                                <>
                                  <span className="spinner" />
                                  Deleting…
                                </>
                              ) : (
                                "Delete"
                              )}
                            </button>

                            {isTrialing(user) && (
                              <>
                                <input
                                  type="number"
                                  min={1}
                                  max={30}
                                  value={extraDaysMap[user.email] ?? 7}
                                  onChange={(e) =>
                                    setExtraDaysMap((prev) => ({
                                      ...prev,
                                      [user.email]: Number(e.target.value),
                                    }))
                                  }
                                  className="admin-trial-input"
                                  title="Extra days (1–30)"
                                  disabled={!!rowBusy[extKey]}
                                />
                                <button
                                  className="admin-btn"
                                  onClick={() =>
                                    handleExtendTrial(user.email)
                                  }
                                  disabled={!!rowBusy[extKey]}
                                >
                                  {rowBusy[extKey] ? (
                                    <>
                                      <span className="spinner" />
                                      Extending…
                                    </>
                                  ) : (
                                    "Extend Trial"
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No users found.</p>
          )}
        </section>

        {/* Pending principals */}
        <section className="admin-section">
          <h2 className="admin-section-title">Pending Principals</h2>
          {pendingPrincipals.length > 0 ? (
            <>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Email</th>
                      <th>School Name</th>
                      <th>School Website</th>
                      <th>Website Technology</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingPrincipals.map((principal) => {
                      const approveKey = `approve:${principal.email}`;
                      const rejectKey = `reject:${principal.email}`;
                      return (
                        <tr key={principal.email}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedPrincipals.includes(
                                principal.email
                              )}
                              onChange={() =>
                                handleSelectPrincipal(principal.email)
                              }
                            />
                          </td>
                          <td className="cell-ellipsis">
                            {principal.email}
                          </td>
                          <td className="cell-ellipsis">
                            {principal.school_name}
                          </td>
                          <td className="cell-ellipsis">
                            {principal.school_website ? (
                              <a
                                href={principal.school_website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="school-website-link"
                              >
                                {principal.school_website}
                              </a>
                            ) : (
                              "N/A"
                            )}
                          </td>
                          <td>
                            {wpTechLabel(principal.website_tech_used)}
                          </td>
                          <td>
                            {new Date(
                              principal.created_at
                            ).toLocaleString()}
                          </td>
                          <td>
                            <div className="admin-actions">
                              <button
                                onClick={() =>
                                  handleApprovePrincipal(principal.email)
                                }
                                disabled={
                                  doingBulk ||
                                  actionLoading ||
                                  !!rowBusy[approveKey]
                                }
                                className="admin-btn admin-btn--primary"
                              >
                                {rowBusy[approveKey] ? (
                                  <>
                                    <span className="spinner" />
                                    Approving…
                                  </>
                                ) : (
                                  "Approve"
                                )}
                              </button>
                              <button
                                onClick={() =>
                                  handleRejectPrincipal(principal.email)
                                }
                                disabled={
                                  doingBulk ||
                                  actionLoading ||
                                  !!rowBusy[rejectKey]
                                }
                                className="admin-btn admin-btn--danger"
                              >
                                {rowBusy[rejectKey] ? (
                                  <>
                                    <span className="spinner" />
                                    Rejecting…
                                  </>
                                ) : (
                                  "Reject"
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="admin-bulk-actions">
                <button
                  onClick={handleApprovePrincipals}
                  disabled={selectedPrincipals.length === 0 || doingBulk}
                  className="admin-btn admin-btn--primary"
                >
                  {doingBulk ? (
                    <>
                      <span className="spinner" />
                      Approving…
                    </>
                  ) : (
                    "Approve Selected Principals"
                  )}
                </button>
                <button
                  onClick={handleRejectPrincipals}
                  disabled={selectedPrincipals.length === 0 || doingBulk}
                  className="admin-btn admin-btn--danger"
                >
                  {doingBulk ? (
                    <>
                      <span className="spinner" />
                      Rejecting…
                    </>
                  ) : (
                    "Reject Selected Principals"
                  )}
                </button>
              </div>
            </>
          ) : (
            <p>No pending principals found.</p>
          )}
        </section>
      </div>
    </div>
  );
};

export default Admin;
